import type {NegotiationLineChange} from '@afriex/core/utils';
import {feeAmountToInputText, getCorridorKey} from '@afriex/core/utils';

export type NegotiationConfirm =
  'accept' | 'decline' | 'counter' | 'apply' | null;

export type NegotiationAnswerState = {
  /** `counter` shows the price inputs beside the merchant's ask. */
  mode: 'review' | 'counter';
  confirm: NegotiationConfirm;
  /** Raw typed text per corridor key. */
  texts: Record<string, string>;
  counterNote: string;
  rejectionNote: string;
};

export type NegotiationAnswerAction =
  | {type: 'OPEN_COUNTER'; lines: NegotiationLineChange[]}
  | {type: 'CANCEL_COUNTER'}
  | {type: 'SET_TEXT'; key: string; text: string}
  | {type: 'SET_COUNTER_NOTE'; note: string}
  | {type: 'SET_REJECTION_NOTE'; note: string}
  | {type: 'ASK_CONFIRM'; confirm: Exclude<NegotiationConfirm, null>}
  | {type: 'CANCEL_CONFIRM'}
  | {type: 'RESET'};

export const initialNegotiationAnswerState: NegotiationAnswerState = {
  mode: 'review',
  confirm: null,
  texts: {},
  counterNote: '',
  rejectionNote: '',
};

/** The merchant's ask as the text a price box starts with. */
export const askText = (line: NegotiationLineChange): string =>
  feeAmountToInputText(line.proposedAmount);

const negotiationAnswerReducer = (
  state: NegotiationAnswerState,
  action: NegotiationAnswerAction,
): NegotiationAnswerState => {
  switch (action.type) {
    case 'OPEN_COUNTER':
      return {
        ...state,
        mode: 'counter',
        confirm: null,
        texts: Object.fromEntries(
          action.lines.map((line) => [
            getCorridorKey(line.corridor),
            askText(line),
          ]),
        ),
      };
    case 'CANCEL_COUNTER':
      return {
        ...state,
        mode: 'review',
        confirm: null,
        texts: {},
        counterNote: '',
      };
    case 'SET_TEXT':
      return {...state, texts: {...state.texts, [action.key]: action.text}};
    case 'SET_COUNTER_NOTE':
      return {...state, counterNote: action.note};
    case 'SET_REJECTION_NOTE':
      return {...state, rejectionNote: action.note};
    case 'ASK_CONFIRM':
      return {...state, confirm: action.confirm};
    case 'CANCEL_CONFIRM':
      return {...state, confirm: null};
    case 'RESET':
      return initialNegotiationAnswerState;
    default:
      return state;
  }
};

export default negotiationAnswerReducer;
